// Auto Configuração da Entidade (roadmap #46 — backend Contexto/Auto_Configuracao_Entidade.md).
// Assistente em carrossel, tela cheia, montado no App (ao lado dos componentes de
// Mensalidade). Auto-guardado: só abre quando (a) autenticado com entidade ativa,
// (b) o usuário tem permissão de configurar (D3) e (c) existe QUALQUER etapa
// ativa ainda não concluída/pulada — não só as obrigatórias. Não existe
// Só some de vez quando TODAS as etapas foram resolvidas.
//
// Layout de cada slide: painel ESQUERDO com informações/exemplos e painel
// DIREITO com a configuração real (tela embutida, modal de preferências ou
// instrução + "Abrir tela completa", conforme a etapa — ver etapasConteudo.js).
//
// NÃO é um Modal do Bootstrap de propósito: overlay fixo com z-index abaixo dos
// modais (1040 < 1050) para que os modais das telas embutidas abram por cima.
import React, { useCallback, useContext, useEffect, memo, useRef, useState } from "react";
import { Badge, Button, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiClock, FiCopy, FiExternalLink, FiSettings } from "react-icons/fi";
import api from "../../Utils/api";
import { GlobalContext } from "../../Utils/GlobalContext";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import EmpresasConfiguracoesModal from "../Empresas/EmpresasConfiguracoesModal";
import SelecaoPlano from "../Empresas/SelecaoPlano";
import OverviewTour from "../Overview/OverviewTour";
import overviewTours from "../Overview/overviewTours";
import { obterConteudo } from "./etapasConteudo";
import "./AutoConfiguracao.css";

// Fase 3 — etapas com "Incluir Sugestões" (clona cadastros da Empresa Modelo,
// id 1081, ou insere um catálogo padrão fixo quando não há modelo próprio).
// Mesmo conjunto de CLONAGENS_MODELO no backend (autoconfiguracaoRepository).
const CHAVES_COM_MODELO = new Set([
  "tipos_fornecedor",
]);

const CHAVES_MODELO_AUTOMATICO = new Set([
  "classificacao_tipos_documento",
  "tipos_documento",
  "tipos_agendamento",
  "tipos_pagamento",
  "empresas_filiais",
  "indices_correcao",
]);

// Componente isolado com memo: o polling de status não causa re-render aqui,
// evitando que o componente embutido recarregue dados do banco a cada 4s.
// extraProps usa referência estável (constante fora do componente) para não
// quebrar o memo durante o polling.
const EXTRA_PROPS_USUARIOS = Object.freeze({ modoAutoConfig: true });

const EmbedArea = memo(function EmbedArea({ Componente, chave, contador, extraProps }) {
  if (!Componente) return null;
  return (
    <div className="autoconfig-config-embed">
      <Componente key={`${chave}-${contador}`} {...(extraProps || {})} />
    </div>
  );
});

const ROTA_POS_AUTOCONFIGURACAO = "/Estatisticas";
const TIMEOUT_STATUS_MS = 15000;

const obterIndiceEtapaPendente = (st) => {
  if (!Array.isArray(st?.etapas) || st.etapas.length === 0) return 0;
  if (st.proxima_etapa) {
    const idxProxima = st.etapas.findIndex(
      (e) => e.chave === st.proxima_etapa && !e.concluida
    );
    if (idxProxima >= 0) return idxProxima;
  }
  const idxPendente = st.etapas.findIndex((e) => !e.concluida);
  return idxPendente >= 0 ? idxPendente : 0;
};

const todasEtapasResolvidas = (st) =>
  Array.isArray(st?.etapas) && st.etapas.every((e) => e.concluida);

function AutoConfiguracaoAssistente() {
  const { globalData } = useContext(GlobalContext);
  const { toast } = useFeedback();
  const navigate = useNavigate();
  const location = useLocation();
  const emRotaAutoConfiguracao =
    location.pathname.toLowerCase() === "/autoconfiguracao";

  const autenticado = Boolean(localStorage.getItem("authToken"));
  const entidadeId = globalData?.entidade_id || localStorage.getItem("entidade_id");

  const [status, setStatus] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [carregandoStatus, setCarregandoStatus] = useState(false);
  const [erroStatus, setErroStatus] = useState(null);
  const [indice, setIndice] = useState(0);
  const [showPreferencias, setShowPreferencias] = useState(false);
  const [abaPreferencias, setAbaPreferencias] = useState("geral");
  const [planoSelecionadoNaEtapa, setPlanoSelecionadoNaEtapa] = useState(false);
  const [atualizacaoPainelEmbutido, setAtualizacaoPainelEmbutido] = useState(0);
  const carregouParaEntidade = useRef(null);
  const selecaoPlanoRef = useRef(null);
  const modelosAutomaticosAplicados = useRef(new Set());

  const carregarStatus = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_STATUS_MS);
    let resposta;
    try {
      resposta = await api.get(`/api/autoconfiguracao/status`, {
        headers: { id_entidade: entidadeId },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
    if (!resposta?.data) return null;
    const novo = resposta.data?.data || null;
    setStatus(novo);
    return novo;
  }, [entidadeId]);

  // "Concluído" aqui é mais rígido que o `concluido` do backend (que só olha
  // etapas obrigatórias): o assistente só para de reabrir quando TODA etapa
  // ativa foi concluída OU pulada, obrigatória ou não.
  const usarModelo = useCallback(async (chave, { silencioso = false } = {}) => {
    try {
      const resposta = await api.post(
        `/api/autoconfiguracao/usar-modelo`,
        { chave },
        { headers: { id_entidade: entidadeId } }
      );
      const data = resposta?.data || {};
      if (!silencioso) toast.success(data?.message || "Cadastros do modelo copiados.");
      await carregarStatus();
      setAtualizacaoPainelEmbutido((valor) => valor + 1);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || "Erro ao copiar do modelo.");
      console.error("[AutoConfiguracao] Erro ao usar modelo:", err);
      return false;
    }
  }, [carregarStatus, entidadeId, toast]);

  const finalizarAutoConfiguracao = useCallback((st, { somenteSeEmRota = false } = {}) => {
    if (!todasEtapasResolvidas(st)) return false;
    setAberto(false);
    if (!somenteSeEmRota || emRotaAutoConfiguracao) {
      navigate(ROTA_POS_AUTOCONFIGURACAO, { replace: true });
    }
    return true;
  }, [emRotaAutoConfiguracao, navigate]);

  // Bootstrap: consulta o status uma vez por entidade e abre na 1ª etapa pendente.
  useEffect(() => {
    if (!autenticado || !entidadeId) return;
    if (carregouParaEntidade.current === entidadeId) return;
    carregouParaEntidade.current = entidadeId;

    let ativo = true;
    (async () => {
      try {
        setCarregandoStatus(true);
        setErroStatus(null);
        const st = await carregarStatus();
        if (!ativo) return;
        if (!st || !Array.isArray(st.etapas)) {
          setErroStatus(new Error("Status da auto configuracao invalido."));
          return;
        }
        if (finalizarAutoConfiguracao(st, { somenteSeEmRota: true })) return;
        setIndice(obterIndiceEtapaPendente(st));
        setAberto(true);
      } catch (err) {
        // Falha aqui nunca deve travar o app — só loga (gate é conveniência).
        setErroStatus(err);
        console.error("[AutoConfiguracao] Erro ao consultar status:", err);
      } finally {
        if (ativo) setCarregandoStatus(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [autenticado, entidadeId, carregarStatus, finalizarAutoConfiguracao]);

  // As telas embutidas (Usuários, Grupos, Tipos de Documento etc.) são componentes
  // completos e independentes — ao salvar algo lá dentro, nada avisa este assistente.
  // Sem isso, a barra de progresso só se moveria ao trocar de etapa. O polling
  // silencioso mantém a contagem de
  // etapas concluídas — e a barra — evoluindo em tempo real conforme o usuário preenche.
  // Some sozinho assim que a última etapa é resolvida, sem esperar o usuário
  // clicar em "Começar a usar".
  useEffect(() => {
    if (!aberto) return;
    const intervalo = setInterval(() => {
      carregarStatus()
        .then((st) => {
          finalizarAutoConfiguracao(st);
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(intervalo);
  }, [aberto, carregarStatus, finalizarAutoConfiguracao]);

  const statusValido = status !== null && Array.isArray(status.etapas);

  useEffect(() => {
    if (!aberto || !statusValido || !entidadeId) return;
    const etapaAtual = status.etapas[Math.min(indice, status.etapas.length - 1)];
    if (!etapaAtual || etapaAtual.concluida || !CHAVES_MODELO_AUTOMATICO.has(etapaAtual.chave)) return;

    const chaveExecucao = `${entidadeId}:${etapaAtual.chave}`;
    if (modelosAutomaticosAplicados.current.has(chaveExecucao)) return;
    modelosAutomaticosAplicados.current.add(chaveExecucao);

    usarModelo(etapaAtual.chave, { silencioso: true }).then((ok) => {
      if (!ok) modelosAutomaticosAplicados.current.delete(chaveExecucao);
    });
  }, [aberto, entidadeId, indice, status, statusValido, usarModelo]);

  // Reabrir só pela rota (/AutoConfiguracao) sem passar pelo bootstrap — ex.:
  // "Começar a usar" fechou sem tudo resolvido e a rota continua aqui — não
  // pode reaproveitar o `indice` antigo (ficaria preso na última etapa
  // visitada). Sempre recalcula a 1ª pendente antes de exibir.
  useEffect(() => {
    if (aberto || !statusValido || !emRotaAutoConfiguracao) return;
    if (todasEtapasResolvidas(status)) {
      navigate(ROTA_POS_AUTOCONFIGURACAO, { replace: true });
      return;
    }
    setIndice(obterIndiceEtapaPendente(status));
    setAberto(true);
  }, [aberto, statusValido, emRotaAutoConfiguracao, status, navigate]);

  const deveExibirAssistente = statusValido && aberto;

  if (!statusValido) {
    if (!emRotaAutoConfiguracao) return null;
    return (
      <div className="autoconfig-overlay" role="dialog" aria-label={"Auto configura\u00e7\u00e3o da entidade"}>
        <div className="autoconfig-config-card autoconfig-config-acao">
          {erroStatus ? (
            <>
              <h3>{"Auto configura\u00e7\u00e3o da entidade"}</h3>
              <p>{"N\u00e3o foi poss\u00edvel carregar as etapas da configura\u00e7\u00e3o agora."}</p>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Tentar novamente
              </Button>
            </>
          ) : (
            <>
              <Spinner animation="border" size="sm" />
              <p>{carregandoStatus ? "Carregando configura\u00e7\u00e3o..." : "Preparando configura\u00e7\u00e3o..."}</p>
              <small className="text-muted text-center">
                {"Se demorar mais que o normal, a consulta sera interrompida automaticamente."}
              </small>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!deveExibirAssistente) {
    return null;
  }

  const etapas = status.etapas;
  const total = etapas.length;
  const atual = etapas[Math.min(indice, total - 1)];
  const conteudo = obterConteudo(atual.chave) || {};
  const concluidas = etapas.filter((e) => e.concluida).length;
  const proximaBloqueada =
    (atual.chave === "plano" && !atual.concluida && !planoSelecionadoNaEtapa);

  // Fecha só a exibição atual — não persiste nada. Um F5, uma aba nova ou
  // qualquer novo carregamento da página reabre enquanto houver etapa
  // pendente (ver comentário no topo do arquivo).
  const fecharAssistente = () => {
    setAberto(false);
    if (todasEtapasResolvidas(status)) {
      navigate(ROTA_POS_AUTOCONFIGURACAO, { replace: true });
    }
  };

  const revalidar = async () => {
    try {
      const st = await carregarStatus();
      finalizarAutoConfiguracao(st);
    } catch (err) {
      console.error("[AutoConfiguracao] Erro ao verificar etapa:", err);
    }
  };

  const marcarEtapa = async (chave, acao) => {
    try {
      await api.post(
        `/api/autoconfiguracao/marcar`,
        { chave, acao },
        { headers: { id_entidade: entidadeId } }
      );
      const st = await carregarStatus();
      finalizarAutoConfiguracao(st);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || "Erro ao atualizar a etapa.");
      console.error("[AutoConfiguracao] Erro ao marcar etapa:", err);
      return false;
    }
  };

  const irPara = (novoIndice) => {
    setPlanoSelecionadoNaEtapa(false);
    setIndice(Math.max(0, Math.min(total - 1, novoIndice)));
    // Recarrega em segundo plano para refletir configurações feitas no painel.
    revalidar();
  };

  const concluirEtapaAtual = async () => {
    if (atual.concluida) return true;
    if (atual.chave === "documentos") return true;
    return marcarEtapa(atual.chave, "concluir");
  };

  const handleProxima = async () => {
    if (atual.chave === "plano") {
      const seletorPlano = selecaoPlanoRef.current;
      if (!planoSelecionadoNaEtapa) {
        toast.warning("Selecione um plano antes de avançar.");
        return;
      }
      if (seletorPlano?.temAssinaturaPendenteBloqueando?.()) {
        toast.warning("Conclua o pagamento pendente antes de escolher outro plano.");
        return;
      }
      if (seletorPlano?.precisaFormaPagamento?.()) {
        toast.warning("Selecione a forma de pagamento da assinatura antes de avan\u00e7ar.");
        return;
      }
      if (seletorPlano?.temPlanoPendente?.()) {
        try {
          const resultado = await seletorPlano.aplicarPlanoPendente();
          if (resultado === null) return;
          if (resultado?.message) toast.success(resultado.message);
          await revalidar();
        } catch (err) {
          toast.error(err.response?.data?.message || "Erro ao aplicar o plano selecionado.");
          return;
        }
      }
    }
    const ok = await concluirEtapaAtual();
    if (!ok) return;
    if (indice < total - 1) irPara(indice + 1);
  };

  const handleMarcarConfigurada = () => concluirEtapaAtual().then((ok) => {
    if (ok && indice < total - 1) irPara(indice + 1);
  });

  const handleUsarModelo = async () => {
    await usarModelo(atual.chave);
  };

  const handleAbrirTelaCompleta = () => {
    // Navegar por baixo do overlay não funciona — fecha o assistente agora
    // (reabre no próximo carregamento da página enquanto houver pendência).
    fecharAssistente();
    if (atual.rota) navigate(atual.rota);
  };

  const abrirPreferencias = () => {
    setAbaPreferencias(conteudo.abaInicial || "geral");
    setShowPreferencias(true);
  };

  const ComponenteEmbutido = conteudo.Componente || null;

  return (
      <div className="autoconfig-overlay" role="dialog" aria-label={"Auto configura\u00e7\u00e3o da entidade"}>
      <header className="autoconfig-header">
        <div className="autoconfig-header-titulo">
          <div className="autoconfig-header-icon">
            <FiSettings size={20} aria-hidden="true" />
          </div>
          <div>
            <span className="autoconfig-eyebrow">{"Auto configura\u00e7\u00e3o da entidade"}</span>
            <h2>{atual.titulo}</h2>
          </div>
        </div>
        <div className="autoconfig-header-meta">
          {`${concluidas} de ${total} conclu\u00edda${concluidas === 1 ? "" : "s"}`}
        </div>
      </header>
      <div
        className="autoconfig-stepper"
        role="progressbar"
        aria-valuenow={indice + 1}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-label="Progresso da configura\u00e7\u00e3o"
      >
        {etapas.map((etapa, idx) => {
          const isConcluida = etapa.concluida;
          const isAtual = idx === Math.min(indice, total - 1);
          return (
            <React.Fragment key={etapa.chave}>
              {idx > 0 && (
                <div className={`autoconfig-stepper-linha${isConcluida || idx <= indice ? " ativa" : ""}`} />
              )}
              <button
                className={`autoconfig-stepper-step${isConcluida ? " concluida" : ""}${isAtual ? " atual" : ""}`}
                onClick={() => irPara(idx)}
                title={etapa.titulo}
                aria-label={`${etapa.titulo}${isConcluida ? " (concluída)" : isAtual ? " (atual)" : ""}`}
                aria-current={isAtual ? "step" : undefined}
              >
                <span className="autoconfig-stepper-circulo">
                  {isConcluida ? <FiCheckCircle size={11} aria-hidden="true" /> : idx + 1}
                </span>
                <span className="autoconfig-stepper-label">{etapa.titulo}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div className="autoconfig-body">
        <aside className="autoconfig-painel-info">
          <div className="autoconfig-info-card">
            <div className="autoconfig-info-status">
              {atual.concluida ? (
                <Badge bg="success" pill>
                  <FiCheckCircle size={12} /> {"Etapa conclu\u00edda"}
                </Badge>
              ) : (
                <Badge bg={atual.obrigatoria ? "warning" : "secondary"} pill>
                  <FiClock size={12} /> {atual.obrigatoria ? "Obrigat\u00f3ria" : "Opcional"}
                </Badge>
              )}
            </div>
            <div className="autoconfig-info-conteudo">{conteudo.info || null}</div>
          </div>
        </aside>

        <main className="autoconfig-painel-config">
          {conteudo.tipo === "resumo" && (
            <div className="autoconfig-config-card">
              <h3>O que vamos configurar</h3>
              <ul className="autoconfig-checklist" data-tour="autoconfig-visao-geral-checklist">
                {etapas.map((e) => (
                  <li key={e.chave}>
                    {e.concluida ? (
                      <FiCheckCircle className="autoconfig-check-ok" aria-hidden="true" />
                    ) : (
                      <FiClock className="autoconfig-check-pendente" aria-hidden="true" />
                    )}
                    <span>{e.titulo}</span>
                    {!e.obrigatoria && <Badge bg="light" text="dark">opcional</Badge>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {conteudo.tipo === "modal-preferencias" && atual.chave === "plano" && (
            <div className="autoconfig-config-card">
              <SelecaoPlano
                ref={selecaoPlanoRef}
                idEntidade={entidadeId}
                standalone
                mostrarConfirmacaoStandalone={false}
                onPlanoAplicado={revalidar}
                onPlanoSelecionadoChange={setPlanoSelecionadoNaEtapa}
              />
            </div>
          )}

          {conteudo.tipo === "modal-preferencias" && atual.chave !== "plano" && (
            <div className="autoconfig-config-card autoconfig-config-acao">
              <p>A configuração desta etapa é feita nas Preferências da Entidade.</p>
              <Button variant="primary" data-tour="autoconfig-preferencias-abrir" onClick={abrirPreferencias}>
                <FiSettings size={15} /> Abrir preferências
              </Button>
            </div>
          )}

          {conteudo.tipo === "manual" && (
            <div className="autoconfig-config-card autoconfig-config-acao">
              <p>
                Esta configuração é feita na tela própria da rotina. Ao abrir a
                tela completa, o assistente fecha e volta no seu próximo acesso.
              </p>
              {atual.rota && (
                <Button variant="outline-primary" onClick={handleAbrirTelaCompleta}>
                  <FiExternalLink size={15} /> Abrir tela completa
                </Button>
              )}
            </div>
          )}

          <EmbedArea
            Componente={ComponenteEmbutido}
            chave={atual.chave}
            contador={atualizacaoPainelEmbutido}
            extraProps={atual.chave === "usuarios" ? EXTRA_PROPS_USUARIOS : undefined}
          />
        </main>
      </div>

      <footer className="autoconfig-footer">
        <div className="autoconfig-footer-esquerda" />
        <div className="autoconfig-footer-centro">
          {!atual.concluida && CHAVES_COM_MODELO.has(atual.chave) && (
            <Button
              variant="light"
              size="sm"
              data-tour="autoconfig-incluir-sugestoes"
              className="modal-footer-btn-acao btn-acao-sugestao"
              onClick={handleUsarModelo}
              title={
                atual.chave === "empresas_filiais"
                  ? "Cadastrar uma filial com os mesmos dados (raz\u00e3o social, fantasia e CNPJ) da entidade"
                  : "Incluir sugest\u00f5es de configura\u00e7\u00e3o com base no modelo padr\u00e3o"
              }
            >
              <FiCopy size={15} className="me-1" />{" "}
              {atual.chave === "empresas_filiais" ? "Incluir Entidade" : "Incluir Sugest\u00f5es"}
            </Button>
          )}
          {atual.chave === "fornecedores" && (
            <Button
              variant="light"
              size="sm"
              data-tour="autoconfig-marcar-configurada"
              className="modal-footer-btn-acao btn-acao-agenda"
              onClick={handleMarcarConfigurada}
              disabled={atual.concluida}
              title={atual.concluida ? "Etapa já configurada" : "Marcar como configurada"}
            >
              <FiCheckCircle size={15} className="me-1" />
              {atual.concluida ? "Etapa configurada" : "Marcar como configurada"}
            </Button>
          )}
        </div>
        <div className="autoconfig-footer-direita">
          <div className="autoconfig-footer-direita-nav">
            <Button className="btn-app-cancelar" onClick={() => irPara(indice - 1)} disabled={indice === 0}>
              <FiArrowLeft size={15} /> Anterior
            </Button>
            {indice < total - 1 ? (
              <Button
                className="btn-app-primary"
                data-tour="autoconfig-visao-geral-proxima"
                onClick={handleProxima}
                disabled={proximaBloqueada}
              >
                {"Pr\u00f3xima"} <FiArrowRight size={15} />
              </Button>
            ) : (
              <Button
                className="btn-app-salvar"
                onClick={fecharAssistente}
                disabled={atual.chave === "documentos" && !atual.concluida}
                title={
                  atual.chave === "documentos" && !atual.concluida
                    ? "Cadastre o primeiro documento para concluir a auto configura\u00e7\u00e3o"
                    : undefined
                }
              >
                <FiCheckCircle size={15} /> {"Come\u00e7ar a usar"}
              </Button>
            )}
          </div>
        </div>
      </footer>

      {showPreferencias && (
        <EmpresasConfiguracoesModal
          show={showPreferencias}
          handleClose={() => {
            setShowPreferencias(false);
            revalidar();
          }}
          empresaId={entidadeId}
          entidadeID={entidadeId}
          empresaNome={globalData?.nome_entidade || "Entidade"}
          abaInicial={abaPreferencias}
        />
      )}

      {atual.chave === "visao_geral" && (
        <OverviewTour {...overviewTours.visaoGeral} executar={deveExibirAssistente} />
      )}

      {atual.chave === "preferencias_entidade" && (
        <OverviewTour {...overviewTours.preferenciasEntidade} executar={deveExibirAssistente} />
      )}

      {atual.chave === "usuarios" && (
        <OverviewTour {...overviewTours.usuarios} executar={deveExibirAssistente} />
      )}

      {atual.chave === "grupos_usuario" && (
        <OverviewTour {...overviewTours.gruposUsuario} executar={deveExibirAssistente} />
      )}

      {atual.chave === "tipos_agendamento" && (
        <OverviewTour {...overviewTours.tiposAgendamento} executar={deveExibirAssistente} />
      )}

      {atual.chave === "tipos_pagamento" && (
        <OverviewTour {...overviewTours.tiposPagamento} executar={deveExibirAssistente} />
      )}

      {atual.chave === "fornecedores" && (
        <OverviewTour {...overviewTours.fornecedores} executar={deveExibirAssistente} />
      )}

      {atual.chave === "tipos_fornecedor" && (
        <OverviewTour {...overviewTours.tiposFornecedor} executar={deveExibirAssistente} />
      )}

      {atual.chave === "empresas_filiais" && (
        <OverviewTour {...overviewTours.filiais} executar={deveExibirAssistente} />
      )}

      {atual.chave === "funcionarios" && (
        <OverviewTour {...overviewTours.funcionarios} executar={deveExibirAssistente} />
      )}

      {atual.chave === "indices_correcao" && (
        <OverviewTour {...overviewTours.indicesCorrecao} executar={deveExibirAssistente} />
      )}

      {atual.chave === "campos_customizados" && (
        <OverviewTour {...overviewTours.camposCustomizados} executar={deveExibirAssistente} />
      )}

      {atual.chave === "email_notificacao" && (
        <OverviewTour {...overviewTours.emailNotificacao} executar={deveExibirAssistente} />
      )}

      {atual.chave === "classificacao_tipos_documento" && (
        <OverviewTour {...overviewTours.classificacaoTiposDocumento} executar={deveExibirAssistente} />
      )}

      {atual.chave === "tipos_documento" && (
        <OverviewTour {...overviewTours.tiposDocumento} executar={deveExibirAssistente} />
      )}
    </div>
  );
}

export default AutoConfiguracaoAssistente;
